import type { QueuedPhotoEntry } from './photoQueueStorage'

export const PHOTO_MAX_ATTEMPTS = 8
const BACKOFF_BASE_MS = 30_000
const BACKOFF_CAP_MS = 3_600_000

/** 30s · 2^attempts, capped at 1h. */
export const photoBackoffMs = (attempts: number): number =>
  Math.min(BACKOFF_BASE_MS * 2 ** attempts, BACKOFF_CAP_MS)

export type PhotoQueueStorage = {
  list: () => Promise<QueuedPhotoEntry[]>
  save: (entry: QueuedPhotoEntry) => Promise<void>
  remove: (clientId: string) => Promise<void>
  readBlobs: (
    clientId: string,
    contentType: string,
  ) => Promise<{ photo: Blob; thumb: Blob }>
}

export type PhotoUploader = {
  presign: (
    entry: QueuedPhotoEntry,
  ) => Promise<{ photoUploadUrl: string; thumbUploadUrl: string }>
  put: (url: string, blob: Blob, contentType: string) => Promise<void>
  /** Must be idempotent server-side (it is: create dedupes on clientId). */
  create: (entry: QueuedPhotoEntry) => Promise<void>
}

export type DrainResult = {
  uploaded: QueuedPhotoEntry[]
  remaining: QueuedPhotoEntry[]
}

/**
 * Upload every due queued photo, FIFO, one in flight. Presigns at flush time
 * (upload URLs expire in ~5 min, so they can never be minted at enqueue).
 * Failures back off exponentially; after PHOTO_MAX_ATTEMPTS the entry is
 * marked `failed` but is still retried by forced syncs and NEVER deleted —
 * an operator's photo must not be lost.
 */
export async function drainPhotoQueue(deps: {
  storage: PhotoQueueStorage
  uploader: PhotoUploader
  isOnline: () => boolean
  /** Ignore backoff windows (manual "Sync now"). */
  force?: boolean
  /**
   * Operator bound to the current session. Entries stamped with a different
   * userId are skipped — uploading them would credit this operator with
   * another operator's photos (shared devices).
   */
  ownerId?: number | null
  /** Organization of the current session; entries from other orgs are skipped. */
  ownerTid?: number | null
}): Promise<DrainResult> {
  const {
    storage,
    uploader,
    isOnline,
    force = false,
    ownerId = null,
    ownerTid = null,
  } = deps

  const entries = await storage.list()
  const uploaded: QueuedPhotoEntry[] = []

  for (const entry of entries) {
    if (!isOnline()) break

    // Exact match only — an unowned (null) entry is skipped too: uploading
    // it would credit the current operator with a photo nobody can attribute
    const belongsToAnotherOperator = entry.userId !== ownerId
    if (belongsToAnotherOperator) continue
    // Same rule for the tenant: order 42 in org A is not order 42 in org B
    const belongsToAnotherOrg = entry.tid !== ownerTid
    if (belongsToAnotherOrg) continue

    const dueAt = entry.nextAttemptAtIso
      ? Date.parse(entry.nextAttemptAtIso)
      : 0
    if (!force && dueAt > Date.now()) continue

    try {
      await storage.save({ ...entry, state: 'uploading' })
      const { photo, thumb } = await storage.readBlobs(
        entry.clientId,
        entry.contentType,
      )
      const urls = await uploader.presign(entry)
      await Promise.all([
        uploader.put(urls.photoUploadUrl, photo, entry.contentType),
        uploader.put(urls.thumbUploadUrl, thumb, 'image/jpeg'),
      ])
      // create is idempotent on clientId, so a crash after create but before
      // remove just re-runs into the existing row next drain.
      await uploader.create(entry)
      await storage.remove(entry.clientId)
      uploaded.push(entry)
    } catch (error) {
      console.error('[photoQueue] upload failed', entry.clientId, error)
      const attempts = entry.attempts + 1
      await storage.save({
        ...entry,
        attempts,
        nextAttemptAtIso: new Date(
          Date.now() + photoBackoffMs(attempts),
        ).toISOString(),
        lastError: error instanceof Error ? error.message : String(error),
        state: attempts >= PHOTO_MAX_ATTEMPTS ? 'failed' : 'queued',
      })
    }
  }

  const remaining = await storage.list()
  return { uploaded, remaining }
}
